import { errorToString } from "@metriport/shared";
import { SetStateAction } from "react";
import {
  checkMapiAccess,
  getCoverageAssessment,
  getCqFacility,
  getCqOrganization,
  getCwFacility,
  getCwOrganization,
  getCxCohorts,
  getCxData,
  getHieEnabledStatus,
} from "../../api/internal";
import { Cohort } from "../../api/schemas/cohort";
import { ExtendedFacility, Facility } from "../../api/schemas/facility";
import {
  CqDirectoryEntryData,
  CwOrgData,
  ExtendedOrganization,
  Organization,
} from "../../api/schemas/organization";
import { PatientCoverage } from "../../api/schemas/patient";
import { CxFeatureFlagDetails, CxFeatureFlagStatus } from "../../domain/app-config";
import { createCustomerAndRootMaps, CustomerMapEntry } from "../../domain/customer";
import { FacilitiesMap } from "../../domain/facility";
import {
  AppState,
  AppStateAction,
  AppStateActionType,
  AppStateLoaded,
} from "../contexts/app/reducer";
import { Toast } from "./toast";
import { captureAndDisplayError } from "./util";

export function baseCxHook({
  state,
  dispatch,
  cxId,
  setCustomer,
  setIsRoot,
  setCwStatus,
  setCqStatus,
  setEhexStatus,
  setEpicStatus,
  setDemoAugStatus,
  setAdtRosterUploadStatus,
  setAdtDataVisibleStatus,
  setHasMapiAccess,
  toast,
}: {
  state: AppStateLoaded;
  dispatch: React.Dispatch<AppStateAction>;
  cxId: string;
  setCustomer: React.Dispatch<SetStateAction<CustomerMapEntry | undefined>>;
  setIsRoot: React.Dispatch<SetStateAction<boolean | undefined>>;
  setCwStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setCqStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEhexStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEpicStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setDemoAugStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtRosterUploadStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtDataVisibleStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setHasMapiAccess?: React.Dispatch<SetStateAction<boolean | undefined>>;
  toast: Toast;
}) {
  const customerInState = (state.customerMap ?? {})[cxId];
  if (customerInState !== undefined) {
    setCustomer(customerInState);
  }
  const isRootInState = (state.rootMap ?? {})[cxId];
  if (isRootInState !== undefined) {
    setIsRoot(isRootInState);
  }
  let isCwInState: CxFeatureFlagDetails | undefined;
  if (setCwStatus !== undefined) {
    isCwInState = (state.cwStatusMap ?? {})[cxId];
    if (isCwInState !== undefined) {
      setCwStatus(isCwInState);
    }
  }
  let isCqInState: CxFeatureFlagDetails | undefined;
  if (setCqStatus !== undefined) {
    isCqInState = (state.cqStatusMap ?? {})[cxId];
    if (isCqInState !== undefined) {
      setCqStatus(isCqInState);
    }
  }
  let isEhexInState: CxFeatureFlagDetails | undefined;
  if (setEhexStatus !== undefined) {
    isEhexInState = (state.ehexStatusMap ?? {})[cxId];
    if (isEhexInState !== undefined) {
      setEhexStatus(isEhexInState);
    }
  }
  let isEpicInState: CxFeatureFlagDetails | undefined;
  if (setEpicStatus !== undefined) {
    isEpicInState = (state.epicStatusMap ?? {})[cxId];
    if (isEpicInState !== undefined) {
      setEpicStatus(isEpicInState);
    }
  }
  let isDemoAugInState: CxFeatureFlagDetails | undefined;
  if (setDemoAugStatus !== undefined) {
    isDemoAugInState = (state.demoAugStatusMap ?? {})[cxId];
    if (isDemoAugInState !== undefined) {
      setDemoAugStatus(isDemoAugInState);
    }
  }
  let isAdtRosterUploadInState: CxFeatureFlagDetails | undefined;
  if (setAdtRosterUploadStatus !== undefined) {
    isAdtRosterUploadInState = (state.adtRosterUploadStatusMap ?? {})[cxId];
    if (isAdtRosterUploadInState !== undefined) {
      setAdtRosterUploadStatus(isAdtRosterUploadInState);
    }
  }
  let isAdtDataVisibleInState: CxFeatureFlagDetails | undefined;
  if (setAdtDataVisibleStatus !== undefined) {
    isAdtDataVisibleInState = (state.adtDataVisibleStatusMap ?? {})[cxId];
    if (isAdtDataVisibleInState !== undefined) {
      setAdtDataVisibleStatus(isAdtDataVisibleInState);
    }
  }
  if (
    ((setCwStatus !== undefined && isCwInState === undefined) ||
      (setCqStatus !== undefined && isCqInState === undefined) ||
      (setEhexStatus !== undefined && isEhexInState === undefined) ||
      (setEpicStatus !== undefined && isEpicInState === undefined) ||
      (setDemoAugStatus !== undefined && isDemoAugInState === undefined) ||
      (setAdtRosterUploadStatus !== undefined && isAdtRosterUploadInState === undefined) ||
      (setAdtDataVisibleStatus !== undefined && isAdtDataVisibleInState === undefined)) &&
    state.userRoles.length > 0
  ) {
    fetchAndSetCxHieEnabledStatus({
      cxId,
      state,
      dispatch,
      setCwStatus,
      setCqStatus,
      setEhexStatus,
      setEpicStatus,
      setDemoAugStatus,
      setAdtRosterUploadStatus,
      setAdtDataVisibleStatus,
      toast,
    });
  }

  if (setHasMapiAccess !== undefined) {
    const hasMapiAccessInState = (state.mapiAccessMap ?? {})[cxId];
    if (hasMapiAccessInState !== undefined) {
      setHasMapiAccess(hasMapiAccessInState);
    } else if (state.userRoles.length > 0) {
      fetchAndSetMapiAccess({
        cxId,
        state,
        dispatch,
        setter: setHasMapiAccess,
        toast,
      });
    }
  }
}

export function cxDataHook({
  state,
  dispatch,
  cxId,
  setOrganization,
  setFacilities,
  setCohorts,
  setLoadingCohorts,
  toast,
  hideHieOrgDisplay,
}: {
  state: AppStateLoaded;
  dispatch: React.Dispatch<AppStateAction>;
  cxId: string;
  setOrganization: React.Dispatch<SetStateAction<ExtendedOrganization | undefined>>;
  setFacilities?: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  setCohorts?: React.Dispatch<SetStateAction<Cohort[] | undefined>>;
  setLoadingCohorts?: React.Dispatch<SetStateAction<boolean>>;
  toast: Toast;
  hideHieOrgDisplay?: boolean;
}) {
  let fetchRan = false;
  const orgInState = (state.organizationMap ?? {})[cxId];
  if (setOrganization && orgInState !== undefined) {
    setOrganization(orgInState);
  } else if (state.userRoles.length > 0) {
    fetchAndSetCxData({
      cxId,
      state,
      dispatch,
      orgSetter: setOrganization,
      facilitiesSetter: setFacilities,
      setCohorts,
      setLoadingCohorts,
      toast,
      hideHieOrgDisplay,
    });
    fetchRan = true;
  }
  if (setFacilities !== undefined) {
    const facilityMapInState = (state.facilityMap ?? {})[cxId];
    if (facilityMapInState !== undefined) {
      setFacilities(facilityMapInState);
    } else if (state.userRoles.length > 0 && !fetchRan) {
      fetchAndSetCxData({
        cxId,
        state,
        dispatch,
        orgSetter: setOrganization,
        facilitiesSetter: setFacilities,
        setCohorts,
        setLoadingCohorts,
        toast,
        hideHieOrgDisplay,
      });
    }
  }
}

export function facilityDataHook({
  state,
  dispatch,
  cxId,
  setFacilities,
  facilityId,
  toast,
}: {
  state: AppStateLoaded;
  dispatch: React.Dispatch<AppStateAction>;
  cxId: string;
  setFacilities: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  facilityId: string;
  toast: Toast;
}) {
  const facilitiesMapInState = (state.facilityMap ?? {})[cxId];
  if (facilitiesMapInState !== undefined) {
    const facilityInState = facilitiesMapInState[facilityId];
    if (
      facilityInState &&
      facilityInState.cwFacility !== undefined &&
      facilityInState.cqFacility !== undefined
    ) {
      setFacilities(facilitiesMapInState);
    } else if (facilityInState && state.userRoles.length > 0) {
      fetchAndSetCxFacilityData({
        cxId,
        state,
        dispatch,
        facilitiesSetter: setFacilities,
        facility: facilityInState.facility,
        facilities: Object.values(facilitiesMapInState).filter(
          f => f !== undefined
        ) as ExtendedFacility[],
        toast,
      });
    }
  }
}

export function facilityPatientDataHook({
  state,
  dispatch,
  cxId,
  setFacilities,
  facilityId,
  toast,
}: {
  state: AppStateLoaded;
  dispatch: React.Dispatch<AppStateAction>;
  cxId: string;
  setFacilities: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  facilityId: string;
  toast: Toast;
}) {
  const facilitiesMapInState = (state.facilityMap ?? {})[cxId];
  if (facilitiesMapInState !== undefined) {
    const facilityInState = facilitiesMapInState[facilityId];
    if (facilityInState && state.userRoles.length > 0) {
      fetchAndSetCxFacilityPatientData({
        cxId,
        state,
        dispatch,
        facilitiesSetter: setFacilities,
        facility: facilityInState,
        facilities: Object.values(facilitiesMapInState).filter(
          f => f !== undefined
        ) as ExtendedFacility[],
        toast,
      });
    }
  }
}

export function setCxOrganzation({
  cxId,
  state,
  dispatch,
  setter,
  organization,
  cqOrganization,
  cwOrganization,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter: React.Dispatch<SetStateAction<ExtendedOrganization | undefined>>;
  organization: Organization | null;
  cqOrganization?: CqDirectoryEntryData | null;
  cwOrganization?: CwOrgData | null;
}): void {
  const extendedOrg = {
    organization,
    cqOrganization,
    cwOrganization,
  };
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      organizationMap: {
        ...state.organizationMap,
        [cxId]: extendedOrg,
      },
    },
  });
  setter(extendedOrg);
}

export function setCxFacility({
  cxId,
  state,
  dispatch,
  setter,
  facility,
  cqFacility,
  cwFacility,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter: React.Dispatch<SetStateAction<Facility | undefined>>;
  facility: Facility;
  cqFacility?: CqDirectoryEntryData | null;
  cwFacility?: CwOrgData | null;
}): void {
  const cxFacilitiesMap = (state.facilityMap ?? {})[cxId];
  const extendedFacility = {
    facility,
    cqFacility,
    cwFacility,
  };
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      facilityMap: {
        ...state.facilityMap,
        [cxId]: {
          ...cxFacilitiesMap,
          [facility.id]: extendedFacility,
        },
      },
    },
  });
  setter(facility);
}

export function setCxFacilities({
  cxId,
  state,
  dispatch,
  setter,
  facilities,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter?: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  facilities: ExtendedFacility[];
}): void {
  const cxFacilitiesMap = Object.fromEntries(
    facilities.map(facility => [facility.facility.id, facility])
  );
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      facilityMap: {
        ...state.facilityMap,
        [cxId]: cxFacilitiesMap,
      },
    },
  });
  if (setter) setter(cxFacilitiesMap);
}

async function fetchAndSetCxFacilityData({
  cxId,
  state,
  dispatch,
  facilitiesSetter,
  facility,
  facilities,
  toast,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  facilitiesSetter?: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  facility: Facility;
  facilities: ExtendedFacility[];
  toast: Toast;
}): Promise<void> {
  async function fetchCqFacility() {
    try {
      return await getCqFacility(cxId, facility.id, facility.oid);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status !== 404) {
        captureAndDisplayError({
          error: errorToString(error),
          msg: "Failed to get CQ organization for Metriport facility",
          context: "fetchAndSetCxFacilityData",
          captureMsg: "getCqFacility failed",
          toast,
        });
      }
    }
    return null;
  }
  async function fetchCwFacility() {
    try {
      return await getCwFacility(cxId, facility.id, facility.oid);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log(`CW Facility Error: `, error);
      if (error.response?.status !== 404) {
        captureAndDisplayError({
          error: errorToString(error),
          msg: "Failed to get CW organization for Metriport facility",
          context: "fetchAndSetCxFacilityData",
          captureMsg: "getCwFacility failed",
          toast,
        });
      }
    }
    return null;
  }
  const [cqFacility, cwFacility] = await Promise.all([fetchCqFacility(), fetchCwFacility()]);
  setCxFacilities({
    cxId,
    state,
    dispatch,
    setter: facilitiesSetter,
    facilities: [
      ...facilities.filter(f => f !== undefined && f.facility.id !== facility.id),
      {
        facility,
        cqFacility,
        cwFacility,
      },
    ],
  });
}

async function fetchAndSetCxFacilityPatientData({
  cxId,
  state,
  dispatch,
  facilitiesSetter,
  facility,
  facilities,
  toast,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  facilitiesSetter?: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  facility: ExtendedFacility;
  facilities: ExtendedFacility[];
  toast: Toast;
}): Promise<void> {
  let patientsWithAssessments: PatientCoverage[] = [];
  try {
    const resp = await getCoverageAssessment({ cxId, facilityId: facility.facility.id });
    patientsWithAssessments = resp.patientsWithAssessments;
  } catch (error) {
    captureAndDisplayError({
      error: errorToString(error),
      msg: "Failed to get facility patients",
      context: "fetchAndSetCxFacilityPatientData",
      captureMsg: "getCoverageAssessment failed",
      toast,
    });
  }
  setCxFacilities({
    cxId,
    state,
    dispatch,
    setter: facilitiesSetter,
    facilities: [
      ...facilities.filter(f => f !== undefined && f.facility.id !== facility.facility.id),
      {
        ...facility,
        patientsWithAssessments,
      },
    ],
  });
}

async function fetchAndSetCxData({
  cxId,
  state,
  dispatch,
  orgSetter,
  facilitiesSetter,
  setCohorts,
  setLoadingCohorts,
  toast,
  hideHieOrgDisplay = false,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  orgSetter: React.Dispatch<SetStateAction<ExtendedOrganization | undefined>>;
  facilitiesSetter?: React.Dispatch<SetStateAction<FacilitiesMap | undefined>>;
  setCohorts?: React.Dispatch<SetStateAction<Cohort[] | undefined>>;
  setLoadingCohorts?: React.Dispatch<SetStateAction<boolean>>;
  toast: Toast;
  hideHieOrgDisplay?: boolean;
}): Promise<void> {
  try {
    const result = await getCxData(cxId);
    const organization = result.org ?? null;
    const facilities = result.facilities;

    setCxOrganzation({
      cxId,
      state,
      dispatch,
      setter: orgSetter,
      organization,
      cqOrganization: null,
      cwOrganization: null,
    });
    setCxFacilities({
      cxId,
      state,
      dispatch,
      setter: facilitiesSetter,
      facilities: facilities.map(facility => ({ facility })),
    });

    if (setCohorts && setLoadingCohorts) {
      fetchAndSetCxCohorts({ cxId, setCohorts, setLoadingCohorts, toast });
    }

    if (organization && organization.businessType === "healthcare_provider" && organization.oid) {
      const orgOid = organization.oid;
      const [cqOrganization, cwOrganization] = await Promise.all([
        fetchAndSetCqOrganization({ cxId, orgOid, toast, hideHieOrgDisplay }),
        fetchAndSetCwOrganization({ cxId, orgOid, toast, hideHieOrgDisplay }),
      ]);
      setCxOrganzation({
        cxId,
        state,
        dispatch,
        setter: orgSetter,
        organization,
        cqOrganization,
        cwOrganization,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response.status !== 404) {
      captureAndDisplayError({
        error: errorToString(error),
        msg: "Failed to get cx data",
        context: "fetchAndSetCxData",
        captureMsg: "getCxData failed",
        toast,
      });
    } else {
      const msg = "Programming error, unhandled 404 on GET cx-data";
      captureAndDisplayError({
        error: errorToString(error),
        msg,
        context: "fetchAndSetCxData",
        captureMsg: "getCxData failed",
        toast,
      });
      throw new Error(msg);
    }
  }
}

export function setCxMapiAccess({
  cxId,
  state,
  dispatch,
  setter,
  hasMapiAccess,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter?: React.Dispatch<SetStateAction<boolean | undefined>>;
  hasMapiAccess: boolean;
}): void {
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      mapiAccessMap: {
        ...state.mapiAccessMap,
        [cxId]: hasMapiAccess,
      },
    },
  });
  if (setter) setter(hasMapiAccess);
}

async function fetchAndSetMapiAccess({
  cxId,
  state,
  dispatch,
  setter,
  toast,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter: React.Dispatch<SetStateAction<boolean | undefined>>;
  toast: Toast;
}): Promise<void> {
  try {
    const result = await checkMapiAccess(cxId);
    const hasMapiAccess = result.hasMapiAccess;
    setCxMapiAccess({
      cxId,
      state,
      dispatch,
      setter,
      hasMapiAccess,
    });
  } catch (error) {
    captureAndDisplayError({
      error: errorToString(error),
      msg: "Failed to get customer mapi access result",
      context: "fetchAndSetMapiAccess",
      captureMsg: "checkMapiAccess failed",
      toast,
    });
  }
}

export function setCxIsRoot({
  cxId,
  state,
  dispatch,
  setter,
  isRoot,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setter?: React.Dispatch<SetStateAction<boolean | undefined>>;
  isRoot: boolean;
}): void {
  const newCustomers = (state.rawCustomers ?? []).map(cx => {
    if (cx.id === cxId) {
      return {
        ...cx,
        isRoot,
      };
    }
    return cx;
  });
  const { customerMap: newCustomerMap, rootMap: newRootMap } =
    createCustomerAndRootMaps(newCustomers);
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      rootMap: newRootMap,
      customerMap: newCustomerMap,
    },
  });
  if (setter) setter(isRoot);
}

export function setCxHieEnabledStatus({
  cxId,
  state,
  dispatch,
  setCwStatus,
  setCqStatus,
  setEhexStatus,
  setEpicStatus,
  setDemoAugStatus,
  setAdtRosterUploadStatus,
  setAdtDataVisibleStatus,
  hieEnabledStatus,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setCwStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setCqStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEhexStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEpicStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setDemoAugStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtRosterUploadStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtDataVisibleStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  hieEnabledStatus: CxFeatureFlagStatus;
}): void {
  const cwEnabled = hieEnabledStatus.cxsWithCWFeatureFlag;
  const cqEnabled = hieEnabledStatus.cxsWithCQDirectFeatureFlag;
  const ehexEnabled = hieEnabledStatus.cxsWithEhexEnabled;
  const epicEnabled = hieEnabledStatus.cxsWithEpicEnabled;
  const demoAugEnabled = hieEnabledStatus.cxsWithDemoAugEnabled;
  const adtRosterUploadEnabled = hieEnabledStatus.cxsWithAdtRosterUploadEnabled;
  const adtDataVisibleEnabled = hieEnabledStatus.cxsWithAdtDataVisibleEnabled;
  dispatch({
    type: AppStateActionType.update,
    newState: {
      ...state,
      cqStatusMap: {
        ...state.cqStatusMap,
        [cxId]: cqEnabled,
      },
      cwStatusMap: {
        ...state.cwStatusMap,
        [cxId]: cwEnabled,
      },
      ehexStatusMap: {
        ...state.ehexStatusMap,
        [cxId]: ehexEnabled,
      },
      epicStatusMap: {
        ...state.epicStatusMap,
        [cxId]: epicEnabled,
      },
      demoAugStatusMap: {
        ...state.demoAugStatusMap,
        [cxId]: demoAugEnabled,
      },
      adtRosterUploadStatusMap: {
        ...state.adtRosterUploadStatusMap,
        [cxId]: adtRosterUploadEnabled,
      },
      adtDataVisibleStatusMap: {
        ...state.adtDataVisibleStatusMap,
        [cxId]: adtDataVisibleEnabled,
      },
    },
  });
  if (setCwStatus) setCwStatus(cwEnabled);
  if (setCqStatus) setCqStatus(cqEnabled);
  if (setEhexStatus) setEhexStatus(ehexEnabled);
  if (setEpicStatus) setEpicStatus(epicEnabled);
  if (setDemoAugStatus) setDemoAugStatus(demoAugEnabled);
  if (setAdtRosterUploadStatus) setAdtRosterUploadStatus(adtRosterUploadEnabled);
  if (setAdtDataVisibleStatus) setAdtDataVisibleStatus(adtDataVisibleEnabled);
}

async function fetchAndSetCxHieEnabledStatus({
  cxId,
  state,
  dispatch,
  setCwStatus,
  setCqStatus,
  setEhexStatus,
  setEpicStatus,
  setDemoAugStatus,
  setAdtRosterUploadStatus,
  setAdtDataVisibleStatus,
  toast,
}: {
  cxId: string;
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  setCwStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setCqStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEhexStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setEpicStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setDemoAugStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtRosterUploadStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  setAdtDataVisibleStatus?: React.Dispatch<SetStateAction<CxFeatureFlagDetails | undefined>>;
  toast: Toast;
}): Promise<void> {
  try {
    const hieEnabledStatus = await getHieEnabledStatus(cxId);
    setCxHieEnabledStatus({
      cxId,
      state,
      dispatch,
      setCwStatus,
      setCqStatus,
      setEhexStatus,
      setEpicStatus,
      setDemoAugStatus,
      setAdtRosterUploadStatus,
      setAdtDataVisibleStatus,
      hieEnabledStatus,
    });
  } catch (error) {
    captureAndDisplayError({
      error: errorToString(error),
      msg: "Failed to get customer hie statuses",
      context: "fetchAndSetCxHieEnabledStatus",
      captureMsg: "getHieEnabledStatus failed",
      toast,
    });
    if (setEhexStatus !== undefined) {
      setEhexStatus({ cxInFFValues: false, ffEnabled: false });
    }
  }
}

async function fetchAndSetCqOrganization({
  cxId,
  orgOid,
  toast,
  hideHieOrgDisplay,
}: {
  cxId: string;
  orgOid: string;
  toast: Toast;
  hideHieOrgDisplay?: boolean;
}): Promise<CqDirectoryEntryData | undefined> {
  try {
    return await getCqOrganization(cxId, orgOid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response.status !== 404) {
      captureAndDisplayError({
        error: errorToString(error),
        msg: "Failed to get CQ organization for Metriport organization",
        context: "fetchAndSetCxData",
        captureMsg: "getCqOrganization failed",
        toast,
        hideDisplay: hideHieOrgDisplay,
      });
    }
  }
}

async function fetchAndSetCwOrganization({
  cxId,
  orgOid,
  toast,
  hideHieOrgDisplay,
}: {
  cxId: string;
  orgOid: string;
  toast: Toast;
  hideHieOrgDisplay?: boolean;
}): Promise<CwOrgData | undefined> {
  try {
    return await getCwOrganization(cxId, orgOid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response.status !== 404) {
      captureAndDisplayError({
        error: errorToString(error),
        msg: "Failed to get CW organization for Metriport organization",
        context: "fetchAndSetCxData",
        captureMsg: "getCwOrganization failed",
        toast,
        hideDisplay: hideHieOrgDisplay,
      });
    }
  }
}

async function fetchAndSetCxCohorts({
  cxId,
  setCohorts,
  setLoadingCohorts,
  toast,
}: {
  cxId: string;
  setCohorts: React.Dispatch<SetStateAction<Cohort[] | undefined>>;
  setLoadingCohorts: React.Dispatch<SetStateAction<boolean>>;
  toast: Toast;
}): Promise<void> {
  try {
    setLoadingCohorts(true);
    const cohorts = await getCxCohorts(cxId);
    setCohorts(cohorts);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    captureAndDisplayError({
      error: errorToString(error),
      msg: "Failed to get customer cohorts",
      context: "fetchAndSetCxCohorts",
      captureMsg: "getCxCohorts failed",
      toast,
    });
  } finally {
    setLoadingCohorts(false);
  }
}
